import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { getShoppingCart } from '../api/endpoints/shoppingCart';
import type { ShoppingCart } from '../api/types/shoppingCart';
import { BookingFlowProvider, useBookingFlowContext } from './BookingFlowProvider';
import type { BookingFlowResumeSeed } from './useBookingFlow';
import type { PersistedBookingFlowConfig, ResumeAdapter } from './resume';
import { clearDraft, loadDraft, type BookingDraft } from './draftStorage';

interface ResumeContextValue {
  resumed: boolean;
  deadlineMs: number | null;
}
const ResumeContext = createContext<ResumeContextValue>({ resumed: false, deadlineMs: null });

/** Read by flow pages that care whether they were resumed and what deadline applies
 *  (currently only `AirFlowInner`). Safe to call outside a resumer — returns the inert
 *  default. */
export function useBookingFlowResume(): ResumeContextValue {
  return useContext(ResumeContext);
}

/** Small "you're continuing an earlier booking" bar with an escape hatch. Rendered
 *  inside the provider so its button can call `flow.reset()`. */
function ResumedBanner() {
  const flow = useBookingFlowContext();
  return (
    <Alert
      severity="info"
      sx={{ mb: 2 }}
      action={
        <Button color="inherit" size="small" onClick={flow.reset}>
          Yeni işlem başlat
        </Button>
      }
    >
      Kaldığınız işlemden devam ediliyor.
    </Alert>
  );
}

export interface UrlResume {
  cartId: string;
  deadlineMs: number | null;
}

interface BookingFlowResumerProps<TState> {
  config: PersistedBookingFlowConfig<TState>;
  adapter: ResumeAdapter<TState>;
  children: ReactNode;
  /** e.g. air's `?resume=<cartId>&expireAt=…`. Wins over a sessionStorage draft. */
  urlResume?: UrlResume;
}

type Source<TState> = {
  stepId: string;
  state: Partial<TState>;
  deadline?: number;
  fromUrl: boolean;
};

export function BookingFlowResumer<TState>({
  config,
  adapter,
  children,
  urlResume,
}: BookingFlowResumerProps<TState>) {
  const { enqueueSnackbar } = useSnackbar();
  const persist = config.persist;
  const toastedRef = useRef(false);

  // Validated draft (or url-supplied synthetic), computed once per mount / urlResume change.
  const { source, discard } = useMemo(() => {
    if (urlResume) {
      const s: Source<TState> = {
        stepId: 'payment',
        state: {},
        deadline: urlResume.deadlineMs ?? undefined,
        fromUrl: true,
      };
      return { source: s, discard: null as null | 'expired' };
    }
    const draft = loadDraft<TState>(persist.key) as BookingDraft<TState> | null;
    if (!draft) return { source: null, discard: null };
    if (draft.v !== persist.version) return { source: null, discard: 'version' as const };
    if (draft.deadline && draft.deadline <= Date.now()) {
      return { source: null, discard: 'expired' as const };
    }
    const s: Source<TState> = {
      stepId: draft.stepId,
      state: draft.state,
      deadline: draft.deadline,
      fromUrl: false,
    };
    return { source: s, discard: null };
  }, [urlResume, persist.key, persist.version]);

  const cartId = useMemo(
    () => (source ? adapter.getCartId(source.state) ?? urlResume?.cartId : undefined),
    [source, adapter, urlResume]
  );

  const canRerunSearch = !!source && !cartId && !!adapter.rerunSearch;

  const cartQuery = useQuery({
    queryKey: ['bookingResume', persist.key, cartId],
    queryFn: () => getShoppingCart(cartId as string),
    enabled: !!cartId,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });

  const searchQuery = useQuery({
    queryKey: ['bookingResumeSearch', persist.key],
    queryFn: () => adapter.rerunSearch!(source!.state),
    enabled: canRerunSearch,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });

  // Side effects for the "can't resume" paths: clear the draft, tell the user once.
  useEffect(() => {
    if (toastedRef.current) return;
    if (discard === 'version') {
      clearDraft(persist.key);
      toastedRef.current = true;
    } else if (discard === 'expired') {
      clearDraft(persist.key);
      enqueueSnackbar('Önceki işlemin süresi doldu, yeniden başlıyorsunuz.', { variant: 'info' });
      toastedRef.current = true;
    } else if (cartId && cartQuery.isError) {
      clearDraft(persist.key);
      enqueueSnackbar('Önceki işleminiz bulunamadı, yeniden başlıyorsunuz.', { variant: 'warning' });
      toastedRef.current = true;
    }
  }, [discard, cartId, cartQuery.isError, persist.key, enqueueSnackbar]);

  const stepIndexOf = (stepId: string): number => {
    const i = config.steps.findIndex((s) => s.id === stepId);
    return i >= 0 ? i : 0;
  };

  // ---- resolve what to mount ----
  let seed: BookingFlowResumeSeed<TState> | undefined;
  let providerKey = `${persist.key}:fresh`;
  let deadlineMs: number | null = null;
  let loading = false;

  if (source && cartId) {
    if (cartQuery.isPending) {
      loading = true;
    } else if (cartQuery.isSuccess) {
      const cart = (cartQuery.data?.shoppingCart ?? {}) as ShoppingCart;
      const stepId = adapter.resolveResumeStepId(cart, source.stepId);
      const merged = {
        ...config.initialState,
        ...source.state,
        ...adapter.applyResumedCart(cart, source.state),
      } as TState;
      seed = { stepIndex: stepIndexOf(stepId), state: merged };
      providerKey = `${persist.key}:${cartId}`;
      deadlineMs = source.deadline ?? null;
      // `isPriceChanged` → user-facing warning enqueued in the effect below.
    }
    // cartQuery.isError → fall through to fresh (draft cleared in the effect above)
  } else if (source) {
    // Pre-Allocate refresh: no cart yet. Re-run the search when the adapter can, else
    // just drop back to step 0 with whatever the user had typed (the search step seeds
    // its form from flow state).
    const prefilled = { ...config.initialState, ...source.state } as TState;
    if (canRerunSearch && searchQuery.isPending) {
      loading = true;
    } else if (canRerunSearch && searchQuery.isSuccess && searchQuery.data) {
      seed = {
        stepIndex: stepIndexOf(persist.searchResultStepId),
        state: { ...prefilled, ...searchQuery.data } as TState,
      };
      providerKey = `${persist.key}:search`;
    } else {
      seed = { stepIndex: 0, state: prefilled };
      providerKey = `${persist.key}:search0`;
    }
  }

  const priceChanged =
    !!source && !!cartId && cartQuery.isSuccess && !!cartQuery.data?.shoppingCart?.isPriceChanged;
  useEffect(() => {
    if (priceChanged) {
      enqueueSnackbar('Sepet fiyatı değişmiş olabilir, lütfen kontrol edin.', { variant: 'warning' });
    }
  }, [priceChanged, enqueueSnackbar]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const resumed = !!seed && seed.stepIndex > 0;

  return (
    <ResumeContext.Provider value={{ resumed, deadlineMs }}>
      <BookingFlowProvider key={providerKey} config={config} resume={seed}>
        {resumed && <ResumedBanner />}
        {children}
      </BookingFlowProvider>
    </ResumeContext.Provider>
  );
}
